import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useSearchParams,
  useNavigate,
} from 'react-router-dom';


const API_BASE =
  '/api/seller/smartfarm/esp32';


export default function Esp32Test() {

  const [searchParams] =
    useSearchParams();

  const navigate =
    useNavigate();


  // ============================================================
  // 스마트팜 ID
  // ============================================================

  const siteId = Number(
    searchParams.get('site_id') || 0
  );

  const deviceId = Number(
    searchParams.get('device_id') || 0
  );


  // ============================================================
  // 상태
  // ============================================================

  const [online, setOnline] =
    useState(false);

  const [esp32Ip, setEsp32Ip] =
    useState('');

  // ============================================================
  // MQTT Discovery 동적 엔티티
  //
  // sensor / binary_sensor / switch를 고정하지 않고
  // Flask가 전달하는 entities 배열을 그대로 렌더링한다.
  // ============================================================

  const [entities, setEntities] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [message, setMessage] =
    useState('');

  const [wsConnected, setWsConnected] =
    useState(false);


  // ============================================================
  // 농장 전체 ESP32 목록 모드
  // device_id가 없을 때 사용
  // ============================================================

  const [devices, setDevices] =
    useState([]);

  const [deviceListLoading, setDeviceListLoading] =
    useState(false);

  const [deviceListMessage, setDeviceListMessage] =
    useState('');

  // 셀러 전용 표시 이름 편집 (실제 MQTT ID/상태와 분리)
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [labelSaving, setLabelSaving] = useState(false);
  const [labelError, setLabelError] = useState('');

  const saveEntityLabel = async () => {
    if (!editingLabel || labelSaving) return;
    const name = labelDraft.trim();
    if (!name || name.length > 60) {
      setLabelError('이름은 1~60자로 입력하세요.');
      return;
    }
    setLabelSaving(true);
    setLabelError('');
    try {
      const response = await fetch(`${API_BASE}/entity-display-name`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: siteId,
          device_id: editingLabel.deviceId,
          entity_id: editingLabel.entityId,
          display_name: name,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || '이름 저장 실패');
      setDevices((previous) => previous.map((device) =>
        Number(device.device_id) !== editingLabel.deviceId ? device : {
          ...device,
          entities: (device.entities || []).map((entity) =>
            entity.entity_id === editingLabel.entityId
              ? { ...entity, display_name: name } : entity),
        }));
      setEditingLabel(null);
    } catch (error) {
      setLabelError(error.message || '이름 저장 실패');
    } finally {
      setLabelSaving(false);
    }
  };


  // ============================================================
  // WebSocket 관리용 Ref
  // ============================================================

  const wsRef =
    useRef(null);

  const reconnectTimerRef =
    useRef(null);

  const reconnectAttemptRef =
    useRef(0);

  const mountedRef =
    useRef(false);

  /*
   * 현재 연결 세대 번호.
   *
   * 이전 WebSocket이 늦게 onclose 되는 경우
   * 새 WebSocket 연결에 영향을 주지 못하게 한다.
   */
  const wsGenerationRef =
    useRef(0);


  // ============================================================
  // 현재 WebSocket 연결 여부 확인
  // ============================================================

  const isWebSocketOpen =
    () => {

      return (
        wsRef.current &&
        wsRef.current.readyState ===
          WebSocket.OPEN
      );
    };


  // ============================================================
  // 동적 엔티티 표시 도우미
  // ============================================================

  const normalizeEntities =
    (value) => {

      if (!Array.isArray(value)) {
        return [];
      }

      return value.filter(
        (entity) =>
          entity &&
          typeof entity === 'object' &&
          entity.entity_id
      );
    };


  const formatEntityValue =
    (entity) => {

      const value = entity?.value;

      if (
        value === null ||
        value === undefined ||
        value === ''
      ) {
        return '-';
      }

      if (
        entity?.domain === 'binary_sensor' ||
        entity?.domain === 'switch'
      ) {

        if (value === true) {
          return 'ON';
        }

        if (value === false) {
          return 'OFF';
        }
      }

      if (
        typeof value === 'number' &&
        Number.isFinite(value)
      ) {

        const unit =
          entity?.unit || '';

        const text =
          Number.isInteger(value)
            ? String(value)
            : value.toFixed(1);

        return unit
          ? `${text} ${unit}`
          : text;
      }

      return entity?.unit
        ? `${String(value)} ${entity.unit}`
        : String(value);
    };


  // ============================================================
  // 농장 전체 ESP32 목록 + 현재 상태 조회
  // ============================================================

  const loadDeviceList =
    useCallback(async (silent = false) => {

      if (!siteId || deviceId) {
        return;
      }

      try {

        if (!silent) {
          setDeviceListLoading(true);
        }

        const devicesResponse =
          await fetch(
            `${API_BASE}/devices?site_id=${siteId}`,
            {
              method: 'GET',
              credentials: 'include',
              cache: 'no-store',
            }
          );

        const devicesData =
          await devicesResponse.json();

        if (
          !devicesResponse.ok ||
          !devicesData.ok
        ) {
          throw new Error(
            devicesData.error ||
            'ESP32 목록을 불러오지 못했습니다.'
          );
        }

        const rows =
          Array.isArray(devicesData.devices)
            ? devicesData.devices
            : [];

        const enriched =
          rows.map((device) => {

            return {
              ...device,
              online: Boolean(
                device.online
              ),
              entities:
                normalizeEntities(
                  device.entities
                ),
            };
          });

        setDevices(enriched);

        setDeviceListMessage(
          `ESP32 ${enriched.length}대`
        );

      } catch (error) {

        console.error(
          '[ESP32 LIST] load failed:',
          error
        );

        setDeviceListMessage(
          error.message ||
          'ESP32 목록 조회 실패'
        );

      } finally {

        if (!silent) {
          setDeviceListLoading(false);
        }
      }

    }, [siteId, deviceId]);


  useEffect(() => {

    if (!siteId || deviceId) {
      return;
    }

    let stopped = false;

    const refresh =
      async (silent = false) => {

        if (stopped) {
          return;
        }

        await loadDeviceList(silent);
      };

    refresh(false);

    const timer =
      setInterval(
        () => refresh(true),
        3000
      );

    return () => {

      stopped = true;

      clearInterval(timer);
    };

  }, [
    siteId,
    deviceId,
    loadDeviceList,
  ]);


  // ============================================================
  // ESP32 장치 삭제
  // ============================================================

  const deleteDevice =
    async (
      targetDeviceId,
      targetDeviceName
    ) => {

      const id =
        Number(
          targetDeviceId || 0
        );

      if (!siteId || !id) {
        return;
      }

      const name =
        targetDeviceName ||
        `Device #${id}`;

      const confirmed =
        window.confirm(
          `${name}을(를) 삭제하시겠습니까?\n`
          + '저장된 설정과 OTA/빌드 데이터도 함께 삭제됩니다.'
        );

      if (!confirmed) {
        return;
      }

      try {

        setDeviceListLoading(true);

        const response =
          await fetch(
            `${API_BASE}/devices/${id}`
            + `?site_id=${siteId}`,
            {
              method: 'DELETE',
              credentials: 'include',
              cache: 'no-store',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.ok
        ) {

          throw new Error(
            data.error ||
            'ESP32 삭제 실패'
          );
        }

        setDeviceListMessage(
          data.message ||
          'ESP32가 삭제되었습니다.'
        );

        await loadDeviceList(
          true
        );

      } catch (error) {

        console.error(
          '[ESP32 DELETE] 삭제 실패:',
          error
        );

        setDeviceListMessage(
          error.message ||
          'ESP32 삭제 실패'
        );

      } finally {

        setDeviceListLoading(false);
      }
    };


  // ============================================================
  // REST 현재 상태 조회
  //
  // 초기 화면 표시 / 수동 새로고침용
  // ============================================================

  const loadCurrentStatus =
    useCallback(async (silent = false) => {

      if (!siteId || !deviceId) {
        return;
      }

      try {

        if (!silent) {
          setLoading(true);
        }

        const sensorResponse =
          await fetch(
            `${API_BASE}/sensors?site_id=${siteId}&device_id=${deviceId}`,
            {
              method: 'GET',
              credentials: 'include',
              cache: 'no-store',
            }
          );

        const sensorData =
          await sensorResponse.json();

        if (
          sensorResponse.ok &&
          sensorData.ok
        ) {

          setEsp32Ip(
            sensorData.esp32_ip || ''
          );

          setOnline(
            Boolean(
              sensorData.online
            )
          );

          setEntities(
            normalizeEntities(
              sensorData.entities
            )
          );
        }

      } catch (error) {

        console.error(
          '[ESP32 REST] 상태 조회 실패:',
          error
        );

      } finally {

        if (
          mountedRef.current &&
          !silent
        ) {
          setLoading(false);
        }
      }

    }, [siteId, deviceId]);


  // ============================================================
  // WebSocket 연결
  //
  // 중요:
  //
  // connectWebSocket 자체를 useEffect dependency로 사용하지 않는다.
  //
  // WebSocket lifecycle 전체를 하나의 useEffect에서 관리한다.
  // ============================================================

  useEffect(() => {

    if (!siteId || !deviceId) {
      return;
    }


    mountedRef.current = true;


    let destroyed = false;


    // ==========================================================
    // WebSocket URL
    // ==========================================================

    const protocol =
      window.location.protocol === 'https:'
        ? 'wss:'
        : 'ws:';


    const wsUrl =
      `${protocol}//` +
      `${window.location.host}` +
      `/api/seller/smartfarm/esp32/ws` +
      `?site_id=${siteId}` +
      `&device_id=${deviceId}`;


    console.log(
      '[ESP32 WS] URL:',
      wsUrl
    );


    // ==========================================================
    // 재연결 타이머 제거
    // ==========================================================

    const clearReconnectTimer =
      () => {

        if (
          reconnectTimerRef.current
        ) {

          clearTimeout(
            reconnectTimerRef.current
          );

          reconnectTimerRef.current =
            null;
        }
      };


    // ==========================================================
    // WebSocket 연결
    // ==========================================================

    const connect =
      () => {

        if (destroyed) {
          return;
        }


        if (!mountedRef.current) {
          return;
        }


        // ------------------------------------------------------
        // 이미 연결 중 / 연결 완료라면 새로 만들지 않는다.
        // ------------------------------------------------------

        if (
          wsRef.current &&
          (
            wsRef.current.readyState ===
              WebSocket.CONNECTING ||
            wsRef.current.readyState ===
              WebSocket.OPEN
          )
        ) {

          console.log(
            '[ESP32 WS] already connected/connecting'
          );

          return;
        }


        clearReconnectTimer();


        // ------------------------------------------------------
        // 기존 CLOSED/CLOSING 객체 제거
        // ------------------------------------------------------

        wsRef.current = null;


        // ------------------------------------------------------
        // 새로운 generation
        // ------------------------------------------------------

        const generation =
          ++wsGenerationRef.current;


        console.log(
          `[ESP32 WS] connecting #${generation}`
        );


        let ws;


        try {

          ws =
            new WebSocket(
              wsUrl
            );

        } catch (error) {

          console.error(
            '[ESP32 WS] 생성 실패:',
            error
          );

          scheduleReconnect();

          return;
        }


        wsRef.current =
          ws;


        // ======================================================
        // 연결 성공
        // ======================================================

        ws.onopen = () => {

          /*
           * 이미 더 최신 연결이 만들어졌다면 무시.
           */
          if (
            generation !==
              wsGenerationRef.current
          ) {

            return;
          }


          console.log(
            `[ESP32 WS] connected #${generation}`
          );


          reconnectAttemptRef.current =
            0;


          if (
            mountedRef.current &&
            !destroyed
          ) {

            setWsConnected(true);

            setMessage(
              '실시간 연결됨'
            );
          }
        };


        // ======================================================
        // 메시지 수신
        // ======================================================

        ws.onmessage = (
          event
        ) => {

          /*
           * 이전 연결의 메시지는 무시.
           */

          if (
            generation !==
              wsGenerationRef.current
          ) {

            return;
          }


          try {

            const data =
              JSON.parse(
                event.data
              );


            console.log(
              '[ESP32 WS]',
              data
            );


            // ==================================================
            // 서버 에러
            // ==================================================

            if (
              data.type === 'error'
            ) {

              if (
                mountedRef.current
              ) {

                setMessage(
                  data.error ||
                  'WebSocket 오류'
                );
              }

              return;
            }


            // ==================================================
            // state가 아니면 무시
            // ==================================================

            if (
              data.type !== 'state'
            ) {

              return;
            }


            // ==================================================
            // ESP32 IP
            // ==================================================

            if (
              data.esp32_ip !==
                undefined
            ) {

              setEsp32Ip(
                data.esp32_ip || ''
              );
            }


            // ==================================================
            // 온라인
            // ==================================================

            if (
              data.online !==
                undefined
            ) {

              setOnline(
                Boolean(
                  data.online
                )
              );
            }


            // ==================================================
            // MQTT Discovery 동적 엔티티
            // ==================================================

            if (
              data.entities !==
                undefined
            ) {

              setEntities(
                normalizeEntities(
                  data.entities
                )
              );
            }


            // ==================================================
            // 정상 상태 수신
            // ==================================================

            setMessage(
              '실시간 상태 수신 중'
            );

          } catch (error) {

            console.error(
              '[ESP32 WS] JSON 처리 실패:',
              event.data,
              error
            );
          }
        };


        // ======================================================
        // 연결 종료
        // ======================================================

        ws.onclose = (
          event
        ) => {

          /*
           * 아주 중요.
           *
           * 이전 WebSocket의 onclose가
           * 현재 연결 상태를 덮어쓰지 못하게 한다.
           */

          if (
            generation !==
              wsGenerationRef.current
          ) {

            console.log(
              `[ESP32 WS] old connection closed #${generation}`
            );

            return;
          }


          console.log(
            `[ESP32 WS] disconnected #${generation}`,
            {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
            }
          );


          if (
            wsRef.current === ws
          ) {

            wsRef.current =
              null;
          }


          if (
            mountedRef.current &&
            !destroyed
          ) {

            setWsConnected(
              false
            );

            setMessage(
              'WebSocket 연결 끊김 · 재연결 중...'
            );
          }


          scheduleReconnect();
        };


        // ======================================================
        // WebSocket 오류
        // ======================================================

        ws.onerror = (
          error
        ) => {

          /*
           * onerror 다음에 보통 onclose가 호출된다.
           *
           * 여기서 별도로 connect()를 호출하지 않는다.
           * 그렇지 않으면 연결이 2개 이상 생길 수 있다.
           */

          console.error(
            `[ESP32 WS] error #${generation}:`,
            error
          );
        };
      };


    // ==========================================================
    // 재연결 예약
    // ==========================================================

    const scheduleReconnect =
      () => {

        if (destroyed) {
          return;
        }


        if (!mountedRef.current) {
          return;
        }


        /*
         * 이미 재연결 타이머가 있으면
         * 또 만들지 않는다.
         */

        if (
          reconnectTimerRef.current
        ) {

          return;
        }


        /*
         * 현재 연결이 살아 있다면
         * 재연결하지 않는다.
         */

        if (
          wsRef.current &&
          (
            wsRef.current.readyState ===
              WebSocket.CONNECTING ||
            wsRef.current.readyState ===
              WebSocket.OPEN
          )
        ) {

          return;
        }


        const attempt =
          reconnectAttemptRef.current;


        const delay =
          Math.min(
            1000 *
              Math.pow(
                2,
                attempt
              ),
            10000
          );


        reconnectAttemptRef.current =
          attempt + 1;


        console.log(
          `[ESP32 WS] reconnect in ${delay}ms`
        );


        reconnectTimerRef.current =
          setTimeout(() => {

            reconnectTimerRef.current =
              null;


            if (
              destroyed ||
              !mountedRef.current
            ) {

              return;
            }


            connect();

          }, delay);
      };


    // ==========================================================
    // 최초 연결
    // ==========================================================

    connect();


    // ==========================================================
    // 초기 REST 상태 조회
    // ==========================================================

    loadCurrentStatus();


    // ==========================================================
    // Cleanup
    // ==========================================================

    return () => {

      console.log(
        '[ESP32 WS] cleanup'
      );


      destroyed = true;

      mountedRef.current = false;


      clearReconnectTimer();


      /*
       * 현재 generation을 무효화한다.
       *
       * 기존 WebSocket의 늦은 onclose/onmessage가
       * 다음 lifecycle에 영향을 주지 못하게 한다.
       */

      wsGenerationRef.current += 1;


      if (
        wsRef.current
      ) {

        try {

          wsRef.current.onopen =
            null;

          wsRef.current.onmessage =
            null;

          wsRef.current.onerror =
            null;

          wsRef.current.onclose =
            null;


          if (
            wsRef.current.readyState ===
              WebSocket.OPEN ||
            wsRef.current.readyState ===
              WebSocket.CONNECTING
          ) {

            wsRef.current.close();
          }

        } catch (error) {

          console.error(
            '[ESP32 WS] cleanup error:',
            error
          );
        }


        wsRef.current =
          null;
      }


      setWsConnected(
        false
      );

    };

  }, [
    siteId,
    deviceId,
    loadCurrentStatus,
  ]);



  // ============================================================
  // REST fallback polling
  //
  // WebSocket이 잠깐 끊겨도 Flask의 최신 MQTT 상태를
  // 3초마다 다시 읽어서 화면 상태를 자동 복구한다.
  //
  // WS   : 즉시 반영
  // REST : 복구 안전망
  // ============================================================

  useEffect(() => {

    if (!siteId || !deviceId) {
      return;
    }

    let stopped = false;

    const refresh =
      async () => {

        if (stopped) {
          return;
        }

        await loadCurrentStatus(
          true
        );
      };

    const timer =
      setInterval(
        refresh,
        3000
      );

    return () => {

      stopped = true;

      clearInterval(
        timer
      );
    };

  }, [
    siteId,
    deviceId,
    loadCurrentStatus,
  ]);


  // ============================================================
  // 동적 Switch 제어
  //
  // ESPHome MQTT Discovery의 command_topic을 Flask에서 찾아
  // 해당 switch에 ON/OFF 명령을 전달한다.
  // ============================================================

  const setDynamicEntityState =
    async (
      entity,
      state
    ) => {

      if (!siteId || !deviceId) {
        return;
      }

      if (!entity?.entity_id) {
        return;
      }

      if (!online) {

        setMessage(
          'ESP32가 오프라인입니다.'
        );

        return;
      }

      try {

        setLoading(true);

        setMessage(
          `${entity.name || '스위치'} ${state.toUpperCase()} 명령 전송 중...`
        );

        const response =
          await fetch(
            `${API_BASE}/entity-command`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',
              },

              credentials: 'include',

              cache: 'no-store',

              body: JSON.stringify({
                site_id: siteId,
                device_id: deviceId,
                entity_id:
                  entity.entity_id,
                state,
              }),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.ok
        ) {

          throw new Error(
            data.error ||
            '스위치 제어 실패'
          );
        }

        setMessage(
          data.message ||
          '스위치 명령 전송됨 · 실제 상태 확인 중'
        );

      } catch (error) {

        console.error(
          '[ESP32 ENTITY] 제어 실패:',
          error
        );

        setMessage(
          error.message ||
          '스위치 제어 실패'
        );

      } finally {

        if (
          mountedRef.current
        ) {

          setLoading(false);
        }
      }
    };


  // ============================================================
  // 스마트팜 ID 없음
  // ============================================================

  if (!siteId) {

    return (
      <div
        style={{
          maxWidth: 600,
          margin: '0 auto',
          padding: 20,
        }}
      >

        <h2>
          수동 접속
        </h2>


        <div
          style={{
            padding: 20,
            border: '1px solid #ddd',
            borderRadius: 16,
            textAlign: 'center',
          }}
        >

          <div
            style={{
              color: '#d32f2f',
              fontWeight: 600,
              marginBottom: 16,
            }}
          >
            스마트팜 정보가 없습니다.
          </div>


          <button
            type="button"
            onClick={() =>
              navigate(
                '/seller/smartfarm'
              )
            }
            style={{
              padding: '10px 16px',
              border: '1px solid #ccc',
              borderRadius: 8,
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            스마트팜 목록으로
          </button>

        </div>

      </div>
    );
  }


  // ============================================================
  // 농장 전체 ESP32 목록 화면
  // ============================================================

  if (!deviceId) {

    return (
      <div
        style={{
          maxWidth: 900,
          margin: '0 auto',
          padding: 20,
        }}
      >

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
          }}
        >

          <div>
            <h2 style={{ margin: 0 }}>
              수동 접속
            </h2>

            <div
              style={{
                marginTop: 6,
                color: '#777',
                fontSize: 14,
              }}
            >
              Site #{siteId} · ESP32 전체 장치
            </div>
          </div>


          <div
            style={{
              display: 'flex',
              gap: 8,
            }}
          >

            <button
              type="button"
              onClick={() =>
                loadDeviceList(false)
              }
              disabled={deviceListLoading}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: 8,
                background: '#fff',
                cursor: 'pointer',
                opacity:
                  deviceListLoading
                    ? 0.5
                    : 1,
              }}
            >
              새로고침
            </button>


            <button
              type="button"
              onClick={() =>
                navigate('/seller/smartfarm')
              }
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: 8,
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              목록
            </button>

          </div>

        </div>


        {deviceListLoading &&
        !devices.length ? (

          <div
            style={{
              padding: 30,
              textAlign: 'center',
              color: '#777',
            }}
          >
            ESP32 목록 불러오는 중...
          </div>

        ) : devices.length === 0 ? (

          <div
            style={{
              padding: 30,
              border: '1px solid #ddd',
              borderRadius: 16,
              textAlign: 'center',
              color: '#777',
            }}
          >
            등록된 ESP32가 없습니다.
          </div>

        ) : (

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 14,
            }}
          >

            {devices.map((device) => {

              const id =
                Number(device.device_id || 0);

              const online =
                Boolean(device.online);

              return (
                <div
                  key={id}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: 16,
                    padding: 18,
                    background: '#fff',
                  }}
                >

                  <div
                    style={{
                      display: 'flex',
                      justifyContent:
                        'space-between',
                      alignItems:
                        'flex-start',
                      gap: 12,
                    }}
                  >

                    <div>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                        }}
                      >
                        {device.device_name ||
                          `ESP32 #${id}`}
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          color: '#777',
                          fontSize: 13,
                        }}
                      >
                        Device #{id}
                      </div>
                    </div>


                    <div
                      style={{
                        color: online
                          ? '#16803c'
                          : '#d32f2f',
                        fontWeight: 700,
                        fontSize: 14,
                      }}
                    >
                      {online
                        ? '● 온라인'
                        : '● 오프라인'}
                    </div>

                  </div>


                  {Array.isArray(device.entities) &&
                  device.entities.length > 0 ? (
                    <div
                      style={{
                        marginTop: 16,
                        display: 'grid',
                        gridTemplateColumns:
                          '1fr 1fr',
                        gap: 8,
                      }}
                    >
                      {device.entities.map((entity) => (
                        <div
                          key={entity.entity_id}
                          role={entity.domain === 'switch' ? 'button' : undefined}
                          tabIndex={entity.domain === 'switch' ? 0 : undefined}
                          title={entity.domain === 'switch' ? '클릭하여 표시 이름 변경' : undefined}
                          onClick={entity.domain === 'switch' ? () => {
                            setEditingLabel({ deviceId: id, entityId: entity.entity_id });
                            setLabelDraft(entity.display_name || entity.name || entity.entity_id);
                            setLabelError('');
                          } : undefined}
                          onKeyDown={entity.domain === 'switch' ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setEditingLabel({ deviceId: id, entityId: entity.entity_id });
                              setLabelDraft(entity.display_name || entity.name || entity.entity_id);
                              setLabelError('');
                            }
                          } : undefined}
                          style={{
                            padding: 10,
                            borderRadius: 10,
                            background: '#f6f6f6',
                            cursor: entity.domain === 'switch' ? 'pointer' : 'default',
                          }}
                        >
                          <div
                            style={{
                              color: '#777',
                              fontSize: 12,
                            }}
                          >
                            {entity.display_name || entity.name || entity.entity_id}{entity.domain === 'switch' ? ' ✎' : ''}
                          </div>

                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 15,
                              fontWeight: 700,
                            }}
                          >
                            {formatEntityValue(entity)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        marginTop: 16,
                        color: '#888',
                        fontSize: 13,
                      }}
                    >
                      발견된 MQTT 엔티티가 없습니다.
                    </div>
                  )}

                  {editingLabel?.deviceId === id && (
                    <div style={{ marginTop: 12, padding: 12, border: '1px solid #ddd', borderRadius: 10 }}>
                      <div style={{ fontSize: 13, marginBottom: 6 }}>릴레이 표시 이름 변경</div>
                      <input
                        autoFocus
                        type="text"
                        maxLength={60}
                        value={labelDraft}
                        onChange={(event) => setLabelDraft(event.target.value)}
                        onKeyDown={(event) => { if (event.key === 'Enter') saveEntityLabel(); }}
                        style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box' }}
                      />
                      {labelError && <div style={{ color: '#d32f2f', fontSize: 12, marginTop: 6 }}>{labelError}</div>}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button type="button" disabled={labelSaving || !labelDraft.trim()} onClick={saveEntityLabel}>저장</button>
                        <button type="button" disabled={labelSaving} onClick={() => setEditingLabel(null)}>취소</button>
                      </div>
                    </div>
                  )}

                  {device.esp32_ip ? (
                    <div
                      style={{
                        marginTop: 8,
                        color: '#888',
                        fontSize: 12,
                      }}
                    >
                      IP: {device.esp32_ip}
                    </div>
                  ) : null}


                  <div
                    style={{
                      marginTop: 16,
                      display: 'flex',
                      gap: 8,
                    }}
                  >

                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/seller/smartfarm/esp32`
                          + `?site_id=${siteId}`
                          + `&device_id=${id}`
                        )
                      }
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        border: 'none',
                        borderRadius: 8,
                        background: '#087f9b',
                        color: '#fff',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      상세 제어
                    </button>


                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/seller/smartfarm/esp32/builder`
                          + `?site_id=${siteId}`
                          + `&device_id=${id}`
                        )
                      }
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        border:
                          '1px solid #ccc',
                        borderRadius: 8,
                        background: '#fff',
                        color: '#333',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Builder
                    </button>


                    <button
                      type="button"
                      onClick={() =>
                        deleteDevice(
                          id,
                          device.device_name
                        )
                      }
                      disabled={
                        deviceListLoading
                      }
                      style={{
                        padding: '10px 12px',
                        border:
                          '1px solid #d32f2f',
                        borderRadius: 8,
                        background: '#fff',
                        color: '#d32f2f',
                        fontWeight: 700,
                        cursor: 'pointer',
                        opacity:
                          deviceListLoading
                            ? 0.5
                            : 1,
                      }}
                    >
                      삭제
                    </button>

                  </div>

                </div>
              );
            })}

          </div>
        )}


        <div
          style={{
            marginTop: 16,
            textAlign: 'right',
            color: '#777',
            fontSize: 13,
          }}
        >
          {deviceListMessage}
        </div>

      </div>
    );
  }


  // ============================================================
  // 화면
  // ============================================================

  return (
    <div
      style={{
        maxWidth: 600,
        margin: '0 auto',
        padding: 20,
      }}
    >

      {/* ======================================================
          헤더
      ====================================================== */}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >

        <div>

          <h2
            style={{
              margin: 0,
            }}
          >
            수동 접속
          </h2>


          <div
            style={{
              marginTop: 6,
              color: '#777',
              fontSize: 14,
            }}
          >
            Home Assistant를 거치지 않고
            ESPHome을 MQTT로 직접 제어합니다.
          </div>

          <div
            style={{
              marginTop: 4,
              color: '#555',
              fontSize: 13,
            }}
          >
            Site #{siteId} · Device #{deviceId || '-'}
          </div>

        </div>


        <button
          type="button"
          onClick={() =>
            navigate(
              '/seller/smartfarm'
            )
          }
          style={{
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: 8,
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          목록
        </button>

      </div>


      {/* ======================================================
          연결 상태
      ====================================================== */}

      <div
        style={{
          padding: 16,
          marginBottom: 16,
          borderRadius: 12,
          background: '#f5f5f5',
        }}
      >

        <div
          style={{
            fontSize: 14,
            color: '#666',
          }}
        >
          스마트팜
        </div>


        <div
          style={{
            marginTop: 4,
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          Site #{siteId}
        </div>


        <div
          style={{
            marginTop: 12,
            fontSize: 14,
            color: '#666',
          }}
        >
          ESP32
        </div>


        <div
          style={{
            marginTop: 4,
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          {esp32Ip || '-'}
        </div>


        <div
          style={{
            marginTop: 8,
            color: online
              ? '#16803c'
              : '#d32f2f',
            fontWeight: 600,
          }}
        >
          {online
            ? '● 온라인'
            : '● 오프라인'}
        </div>


        <div
          style={{
            marginTop: 6,
            fontSize: 13,
            color: wsConnected
              ? '#16803c'
              : '#d32f2f',
          }}
        >
          {wsConnected
            ? 'WebSocket 실시간 연결'
            : 'WebSocket 재연결 중...'}
        </div>

      </div>


      {/* ======================================================
          MQTT Discovery 동적 엔티티
      ====================================================== */}

      {entities.length > 0 ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(2, minmax(0, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          {entities.map((entity) => {

            const isSwitch =
              entity.domain === 'switch';

            const switchOn =
              entity.value === true;

            return (
              <div
                key={entity.entity_id}
                style={{
                  gridColumn:
                    isSwitch
                      ? '1 / -1'
                      : 'auto',
                  padding: 18,
                  border: '1px solid #ddd',
                  borderRadius: 16,
                  background: '#fff',
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    color: '#666',
                  }}
                >
                  {entity.name || entity.entity_id}
                </div>

                <div
                  style={{
                    marginTop: 8,
                    fontSize:
                      isSwitch ? 18 : 24,
                    fontWeight: 700,
                  }}
                >
                  {formatEntityValue(entity)}
                </div>

                {entity.device_class ? (
                  <div
                    style={{
                      marginTop: 5,
                      color: '#999',
                      fontSize: 11,
                    }}
                  >
                    {entity.domain}
                    {' · '}
                    {entity.device_class}
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: 5,
                      color: '#999',
                      fontSize: 11,
                    }}
                  >
                    {entity.domain}
                  </div>
                )}

                {isSwitch &&
                entity.command_topic ? (
                  <div
                    style={{
                      marginTop: 14,
                      display: 'flex',
                      gap: 8,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setDynamicEntityState(
                          entity,
                          'on'
                        )
                      }
                      disabled={
                        loading ||
                        !online ||
                        switchOn
                      }
                      style={{
                        flex: 1,
                        padding: '10px 8px',
                        border: 'none',
                        borderRadius: 8,
                        background: '#22c55e',
                        color: '#fff',
                        fontWeight: 700,
                        cursor: 'pointer',
                        opacity:
                          loading ||
                          !online ||
                          switchOn
                            ? 0.5
                            : 1,
                      }}
                    >
                      ON
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setDynamicEntityState(
                          entity,
                          'off'
                        )
                      }
                      disabled={
                        loading ||
                        !online ||
                        !switchOn
                      }
                      style={{
                        flex: 1,
                        padding: '10px 8px',
                        border: 'none',
                        borderRadius: 8,
                        background: '#555',
                        color: '#fff',
                        fontWeight: 700,
                        cursor: 'pointer',
                        opacity:
                          loading ||
                          !online ||
                          !switchOn
                            ? 0.5
                            : 1,
                      }}
                    >
                      OFF
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            padding: 20,
            marginBottom: 16,
            border: '1px solid #ddd',
            borderRadius: 16,
            color: '#777',
            textAlign: 'center',
          }}
        >
          발견된 MQTT 엔티티가 없습니다.
        </div>
      )}


      {/* ======================================================
          하단
      ====================================================== */}

      <div
        style={{
          marginTop: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >

        <button
          type="button"
          onClick={
            loadCurrentStatus
          }
          disabled={loading}
          style={{
            padding: '10px 16px',
            border: '1px solid #ccc',
            borderRadius: 8,
            background: '#fff',
            cursor: 'pointer',
            opacity:
              loading ? 0.5 : 1,
          }}
        >
          새로고침
        </button>


        <span
          style={{
            color:
              wsConnected
                ? '#555'
                : '#d32f2f',
            fontSize: 14,
            textAlign: 'right',
          }}
        >
          {loading
            ? '처리 중...'
            : message}
        </span>

      </div>

    </div>
  );
}